export type DirectWriteSupport = {
  supported: boolean;
  reason?: string;
  platform?: "desktop" | "android";
  isWindows?: boolean;
  /**
   * On Android, showDirectoryPicker returns read-only handles due to SAF restrictions.
   * When true, the write phase must use showOpenFilePicker to get writable handles.
   */
  needsFilePickerForWrite?: boolean;
  capabilities?: {
    isSecureContext: boolean;
    hasDirectoryPicker: boolean;
    hasOpenFilePicker: boolean;
    hasSaveFilePicker: boolean;
  };
};

export function detectDirectWriteSupport(): DirectWriteSupport {
  if (typeof window === "undefined") {
    return { supported: false, reason: "Unavailable in this environment" };
  }

  const hasDirectoryPicker = typeof (window as any).showDirectoryPicker === "function";
  const hasOpenFilePicker = typeof (window as any).showOpenFilePicker === "function";
  const hasSaveFilePicker = typeof (window as any).showSaveFilePicker === "function";
  const isSecure = !!(window as any).isSecureContext;
  const ua = (typeof navigator !== "undefined" && navigator.userAgent) || "";
  const isAndroid = /Android/i.test(ua);
  const isWindows = /Windows/i.test(ua);
  const isIOS = /iPhone|iPad|iPod/i.test(ua);

  if (!isSecure) {
    return {
      supported: false,
      reason: "Direct Write requires a secure context (HTTPS).",
      platform: isAndroid ? "android" : "desktop",
      isWindows,
      needsFilePickerForWrite: false,
      capabilities: {
        isSecureContext: isSecure,
        hasDirectoryPicker,
        hasOpenFilePicker,
        hasSaveFilePicker,
      },
    };
  }

  // On Android, Direct Write is disabled per user request due to system security limitations
  if (isAndroid) {
    return {
      supported: false,
      reason: "Direct Write is not supported on Android due to system security restrictions. Use 'Download' mode instead.",
      platform: "android",
      isWindows: false,
      needsFilePickerForWrite: false,
      capabilities: {
        isSecureContext: isSecure,
        hasDirectoryPicker,
        hasOpenFilePicker,
        hasSaveFilePicker,
      },
    };
  }

  // Desktop: standard flow with directory picker for both read and write
  if (!hasDirectoryPicker) {
    return {
      supported: false,
      reason: "Directory picker not available in this browser.",
      platform: "desktop",
      isWindows,
      needsFilePickerForWrite: false,
      capabilities: {
        isSecureContext: isSecure,
        hasDirectoryPicker,
        hasOpenFilePicker,
        hasSaveFilePicker,
      },
    };
  }

  return {
    supported: true,
    platform: "desktop",
    isWindows,
    needsFilePickerForWrite: false,
    capabilities: {
      isSecureContext: isSecure,
      hasDirectoryPicker,
      hasOpenFilePicker,
      hasSaveFilePicker,
    },
  };
}

